import { ComponentType, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, StaticProvider } from '@angular/core';
import { ArkikDialogRef } from './arkik-dialog-ref';
import { ArkikDialogContainerComponent } from './arkik-dialog.component';

export const ARKIK_DIALOG_DATA = new InjectionToken<any>('ArkikDialogData');
const DEFAULT_CONFIG: ArkikDialogConfig = {
  width: 700,
  enableClose: false,
  customClass: [],
  providers: [],
};

@Injectable({
  providedIn: 'root',
})
export class ArkikDialogService {
  constructor(
    private _overlay: Overlay,
    private _injector: Injector,
  ) {}

  open<T, D = any, R = any>(component: ComponentType<T>, config: ArkikDialogConfig<D>): ArkikDialogRef<T, R> {
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };

    const overlayConfig = new OverlayConfig({
      hasBackdrop: true,
      width: dialogConfig.width,
      height: dialogConfig.height,
      panelClass: dialogConfig.customClass,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
      disposeOnNavigation: true,
    });

    const overlayRef = this._overlay.create(overlayConfig);

    const containerInjector = Injector.create({
      parent: this._injector,
      providers: [{ provide: ArkikDialogConfig, useValue: dialogConfig }],
    });
    const portal = new ComponentPortal(ArkikDialogContainerComponent, null, containerInjector);
    const containerInstance = overlayRef.attach(portal).instance!;

    const dialogRef = new ArkikDialogRef<T, R>(overlayRef, containerInstance);
    const componentInjector = Injector.create({
      parent: this._injector,
      providers: [
        { provide: ArkikDialogRef, useValue: dialogRef },
        { provide: ARKIK_DIALOG_DATA, useValue: dialogConfig.data },
        ...dialogConfig.providers,
      ],
    });
    const componentPortal = new ComponentPortal(component, null, componentInjector);
    const contentRef = containerInstance.attachComponentPortal<T>(componentPortal);
    dialogRef.componentInstance = contentRef.instance;

    return dialogRef;
  }
}

export class ArkikDialogConfig<T = any> {
  /** The width of the dialog. If a number is provided, pixel units are assumed. */
  width?: number | string;
  /** Custom data to share with dialog instance */
  data?: T;
  /** The height of the dialog */
  height?: number | string;
  /** Custom class of dialog */
  customClass?: string[];
  /** Whether the user can use escape or clicking on the backdrop to close the modal */
  enableClose?: boolean;
  /** Inject custom providers to the component */
  providers?: StaticProvider[];
}
